import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Mail } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { api, ApiError, googleAuthUrl } from "@/lib/api";
import { useAuth, type AuthUser } from "@/lib/auth";
import { env } from "@/lib/env";
import { cn } from "@/lib/utils";
import heroStack from "@/assets/hero-stack.jpg";

export const Route = createFileRoute("/login")({
  head: () => ({
    meta: [
      { title: "Log in — AutoTubber" },
      {
        name: "description",
        content: "Sign in to your AutoTubber creator account.",
      },
    ],
    links: [
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700&family=Syne:wght@700;800&display=swap",
      },
    ],
  }),
  validateSearch: (search: Record<string, unknown>) => ({
    redirect:
      typeof search.redirect === "string" && search.redirect.startsWith("/")
        ? search.redirect
        : undefined,
  }),
  component: LoginPage,
});

function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden>
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 12 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A8.96 8.96 0 0 0 1.25 12c0 1.45.35 2.82.95 4.02l3.64-2.93z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}

function LoginPage() {
  const navigate = useNavigate();
  const { redirect } = Route.useSearch();
  const { ready, user, setSessionUser, refresh } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (ready && user) {
      const dest =
        redirect ||
        (user.role === "ADMIN" ? env.routes.admin : env.routes.dashboard);
      navigate({ to: dest, replace: true });
    }
  }, [ready, user, redirect, navigate]);

  const onSubmit = async (e: { preventDefault: () => void }) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const data = await api<{ user: AuthUser }>(env.paths.authLogin, {
        method: "POST",
        body: { email: email.trim(), password },
      });
      setSessionUser(data.user);
      await refresh();
      toast.success("Welcome back");
      const dest =
        redirect ||
        (data.user.role === "ADMIN" ? env.routes.admin : env.routes.dashboard);
      navigate({ to: dest });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  const continueWithGoogle = () => {
    window.location.href = googleAuthUrl(redirect || env.routes.dashboard);
  };

  return (
    <div
      className="flex min-h-screen flex-col bg-background text-foreground lg:flex-row"
      style={{ fontFamily: '"Outfit", ui-sans-serif, sans-serif' }}
    >
      {/* Brand / visual column */}
      <aside className="relative isolate min-h-[38vh] overflow-hidden lg:min-h-screen lg:w-[52%]">
        <img
          src={heroStack}
          alt=""
          className="absolute inset-0 h-full w-full object-cover animate-login-ken"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(160deg, oklch(0.12 0.02 27 / 78%) 0%, oklch(0.1 0.01 27 / 55%) 45%, oklch(0.08 0 0 / 88%) 100%)",
          }}
        />
        <div className="pointer-events-none absolute -left-24 top-1/4 h-72 w-72 rounded-full bg-primary/25 blur-[100px] animate-login-glow" />

        <div className="relative z-10 flex h-full flex-col justify-end px-8 py-10 sm:px-12 lg:justify-between lg:px-14 lg:py-16">
          <Link
            to="/"
            className="hidden animate-login-in lg:inline-block"
            style={{ fontFamily: '"Syne", ui-sans-serif, sans-serif' }}
          >
            <span className="text-sm font-bold tracking-[0.22em] text-white/70 uppercase">
              AutoTubber
            </span>
          </Link>

          <div className="max-w-md animate-login-in [animation-delay:120ms]">
            <p
              className="text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl"
              style={{ fontFamily: '"Syne", ui-sans-serif, sans-serif' }}
            >
              AutoTubber
            </p>
            <p className="mt-3 max-w-sm text-base text-white/75 sm:text-lg">
              Faceless shorts on auto-pilot.
            </p>
          </div>
        </div>
      </aside>

      {/* Form column */}
      <main className="flex flex-1 items-center justify-center px-6 py-12 sm:px-10 lg:w-[48%] lg:px-16">
        <div className="w-full max-w-sm animate-login-form">
          <Link
            to="/"
            className="mb-8 inline-block text-sm font-bold tracking-[0.22em] text-muted-foreground uppercase lg:hidden"
            style={{ fontFamily: '"Syne", ui-sans-serif, sans-serif' }}
          >
            AutoTubber
          </Link>

          <h1 className="text-3xl font-semibold tracking-tight">Log in</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Access your series dashboard. New here?{" "}
            <Link
              to={env.routes.onboarding}
              className="text-primary underline-offset-4 hover:underline"
            >
              Get started
            </Link>
          </p>

          <Button
            type="button"
            variant="secondary"
            size="lg"
            className="mt-8 w-full"
            onClick={continueWithGoogle}
          >
            <GoogleIcon className="mr-2 h-4 w-4" />
            Continue with Google
          </Button>

          <div className="my-6 flex items-center gap-3 text-xs text-muted-foreground">
            <span className="h-px flex-1 bg-border" />
            or email
            <span className="h-px flex-1 bg-border" />
          </div>

          <form onSubmit={onSubmit} className="space-y-3">
            <Input
              type="email"
              autoComplete="email"
              placeholder="you@example.com"
              className="h-11"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <PasswordInput
              autoComplete="current-password"
              placeholder="Password"
              className="h-11"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <Button
              type="submit"
              className={cn("w-full")}
              size="lg"
              disabled={submitting}
            >
              <Mail className="mr-1 h-4 w-4" />
              {submitting ? "Signing in…" : "Log in"}
            </Button>
          </form>
        </div>
      </main>
    </div>
  );
}
